import { sortByAccessor } from "../../../utils/sortRows";
import { fieldForColumnKey, getFieldValue, plainCustomValue } from "../../../utils/customFieldUtils";

export const NO_COMPANY = "No company";

export const COMPANY_COLUMNS = [
  { key: "name", label: "Company", visible: true },
  { key: "industry", label: "Industry", visible: true },
  { key: "primary_contact", label: "Primary contact", visible: true },
  { key: "projects", label: "Projects", visible: true },
  { key: "value", label: "Value", visible: true },
  { key: "phone", label: "Phone", visible: false },
  { key: "website", label: "Website", visible: false },
  { key: "billing_address", label: "Billing address", visible: false },
  { key: "vat_number", label: "VAT number", visible: false },
  { key: "contacts", label: "Contacts", visible: false },
  { key: "notes", label: "Notes", visible: false },
];

export const CONTACT_COLUMNS = [
  { key: "name", label: "Name", visible: true },
  { key: "company", label: "Company", visible: true },
  { key: "job_title", label: "Job title", visible: true },
  { key: "email", label: "Email", visible: true },
  { key: "phone", label: "Phone", visible: true },
  { key: "department", label: "Department", visible: false },
  { key: "notes", label: "Notes", visible: false },
];

export const TEAM_COLUMNS = [
  { key: "name", label: "Name", visible: true },
  { key: "job_title", label: "Job title", visible: true },
  { key: "email", label: "Email", visible: true },
  { key: "phone", label: "Phone", visible: true },
  { key: "department", label: "Department", visible: true },
];

// Hidden from anyone without view_project_budgets.
export const FINANCE_KEYS = new Set(["value"]);

export const DIRECTORY_TABS = {
  companies: {
    label: "Companies",
    columns: COMPANY_COLUMNS,
    storageKey: "directoryCompanyColumns",
    searchPlaceholder: "Search companies…",
    exportName: "directory-companies",
    groups: { industry: { label: "Industry", get: (c) => c.industry || "No industry" } },
  },
  contacts: {
    label: "Contacts",
    columns: CONTACT_COLUMNS,
    storageKey: "directoryContactColumns",
    searchPlaceholder: "Search contacts…",
    exportName: "directory-contacts",
    groups: {
      company: { label: "Company", get: (c) => c.company_name || NO_COMPANY },
      job_title: { label: "Job title", get: (c) => c.job_title || "No job title" },
    },
  },
  team: {
    label: "Team",
    columns: TEAM_COLUMNS,
    storageKey: "directoryTeamColumns",
    searchPlaceholder: "Search team…",
    exportName: "directory-team",
    groups: { department: { label: "Department", get: (p) => p.department || "No department" } },
  },
};

export const DEFAULT_FILTERS = {
  companies: { search: "", industry: "", groupBy: "", sort: null },
  contacts: { search: "", company: "", groupBy: "company", sort: null },
  team: { search: "", department: "", groupBy: "", sort: null },
};

export const primaryContactOf = (company) => (company.contacts || []).find((c) => c.is_primary) || company.contacts?.[0] || null;

// A contact belongs to a company or, with client_company_id null, to "No company".
export const flattenContacts = (companies, standalone) => [
  ...companies.flatMap((company) => (company.contacts || []).map((c) => ({ ...c, id: c.client_contact_id, company_name: company.company_name }))),
  ...standalone.map((c) => ({ ...c, id: c.client_contact_id, company_name: null })),
];

const includes = (text, q) => (text || "").toString().toLowerCase().includes(q);
const matches = (q, values) => !q || values.some((v) => includes(v, q));

export const filterCompanies = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((c) =>
    (!f.industry || c.industry === f.industry)
    && matches(q, [c.company_name, c.industry, c.website, c.company_phone, c.vat_number, ...(c.contacts || []).flatMap((p) => [p.name, p.email, p.phone])])
  );
};

export const filterContacts = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((c) =>
    (!f.company || (f.company === "none" ? c.client_company_id == null : String(c.client_company_id) === f.company))
    && matches(q, [c.name, c.email, c.phone, c.job_title, c.department, c.company_name])
  );
};

export const filterTeam = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((p) => (!f.department || p.department === f.department) && matches(q, [p.name, p.email, p.phone, p.job_title, p.department]));
};

export const FILTERS = { companies: filterCompanies, contacts: filterContacts, team: filterTeam };

// Plain value of each column: what a header click sorts by and what an export writes.
const ACCESSORS = {
  companies: {
    name: (c) => c.company_name,
    industry: (c) => c.industry,
    primary_contact: (c) => primaryContactOf(c)?.name,
    projects: (c) => Number(c.project_count) || 0,
    value: (c) => Number(c.value) || 0,
    phone: (c) => c.company_phone,
    website: (c) => c.website,
    billing_address: (c) => c.billing_address,
    vat_number: (c) => c.vat_number,
    contacts: (c) => (c.contacts || []).length,
    notes: (c) => c.company_notes,
  },
  contacts: {
    name: (c) => c.name,
    company: (c) => c.company_name,
    job_title: (c) => c.job_title,
    email: (c) => c.email,
    phone: (c) => c.phone,
    department: (c) => c.department,
    notes: (c) => c.notes,
  },
  team: {
    name: (p) => p.name,
    job_title: (p) => p.job_title,
    email: (p) => p.email,
    phone: (p) => p.phone,
    department: (p) => p.department,
  },
};

export const exportColumns = (tab, columns, fields) => columns.map((col) => {
  const field = fieldForColumnKey(fields, col.key);
  return { key: col.key, label: col.label, visible: col.visible, value: field ? (row) => plainCustomValue(field, getFieldValue(row, field)) : ACCESSORS[tab][col.key] };
});

export const sortDirectoryRows = (rows, sort, tab, customAccessor) =>
  (sort ? sortByAccessor(rows, sort, ACCESSORS[tab][sort.key] || customAccessor(sort.key)) : rows);
